import { PINNED_LAYER_SUFFIX, RESPONSIVE_PAGE_CONTAINER_PREFIX, SECTIONS_CONTAINER_PREFIX } from '../constants'
import { Component, ComponentMetaData, SingleLayoutData, Structure } from '@wix/thunderbolt-becky-types'
import {
	PinnedPositions,
	PinnedChildrenByPositionMap,
	createComponent,
	checkIfPinnedComponentAndAddToMap,
	isPinnedComponentAndAddToMap,
} from './utils/pinnedComponentsUtils'
import { assign } from 'lodash'

const PINNED_GROUPS_IDS: Record<PinnedPositions, string> = {
	TOP_LEFT: 'pagePinnedTopLeft',
	TOP_CENTER: 'pagePinnedTopCenter',
	TOP_RIGHT: 'pagePinnedTopRight',
	CENTER_LEFT: 'pagePinnedMiddleLeft',
	CENTER_RIGHT: 'pagePinnedMiddleRight',
	BOTTOM_LEFT: 'pagePinnedBottomLeft',
	BOTTOM_CENTER: 'pagePinnedBottomCenter',
	BOTTOM_RIGHT: 'pagePinnedBottomRight',
}

const getPinnedLayerId = (id: string) => `${id}${PINNED_LAYER_SUFFIX}`

const createStructure = (
	pinnedChildrenByPosition: PinnedChildrenByPositionMap,
	nonPinnedChildren: Array<string>,
	additionalData: ComponentMetaData['pageMeshOverrides'] | ComponentMetaData['popupContainerMeshOverrides'],
	comp: Component,
	isOneDocMigrated: boolean
) => {
	const compId = comp.id
	const compType = comp.componentType
	const isPage = compType === 'Page'
	const isPopupContainer = compType === 'PopupContainer'

	const newComp = {
		...comp,
		components: [],
	} as Component
	const compStructure = { [compId]: newComp }

	const createPinnedLayer = (position: PinnedPositions) => {
		const pinnedChildren = pinnedChildrenByPosition[position]
		if (pinnedChildren) {
			const wrapperId = `${PINNED_GROUPS_IDS[position as PinnedPositions]}-${compId}`

			pinnedChildren.forEach((id) => {
				const pinnedLayerId = getPinnedLayerId(id)
				compStructure[pinnedLayerId] = createComponent(
					pinnedLayerId,
					[id],
					wrapperId,
					'PinnedLayer',
					'Container'
				)
			})

			const pinnedLayersWrapper = createComponent(
				wrapperId,
				pinnedChildren.map((id) => getPinnedLayerId(id)),
				compId,
				'DivWithChildren'
			)

			newComp.components!.push(wrapperId)
			compStructure[wrapperId] = pinnedLayersWrapper
		}
	}

	createPinnedLayer('TOP_LEFT')
	createPinnedLayer('TOP_CENTER')
	createPinnedLayer('TOP_RIGHT')
	createPinnedLayer('CENTER_LEFT')
	createPinnedLayer('CENTER_RIGHT')

	const containerId = `Container${compId}`
	compStructure[containerId] = assign(
		{
			id: containerId,
			components: nonPinnedChildren,
			componentType: 'Group',
			skin: comp.skin,
			type: 'Container',
			parent: compId,
			styleId: '',
			metaData: {
				pageMeshOverrides: isPage && additionalData,
				popupContainerMeshOverrides: isPopupContainer && additionalData,
			},
		},
		isOneDocMigrated ? { layoutQuery: comp.layoutQuery } : { layout: { height: comp.layout.height } }
	) as Component
	newComp.components!.push(containerId)

	createPinnedLayer('BOTTOM_LEFT')
	createPinnedLayer('BOTTOM_CENTER')
	createPinnedLayer('BOTTOM_RIGHT')

	return compStructure
}

const memoize = <Args extends Array<any>, Res>(
	func: (...args: Args) => Res,
	resolver: (...args: Args) => string
): ((...args: Args) => Res) => {
	let prev: Res
	let key: string
	return (...args: Args) => {
		const newKey = resolver(...args)
		if (newKey !== key) {
			key = newKey
			prev = func(...args)
			return prev
		}

		return prev
	}
}

const memoizedCreateStructure = memoize(
	createStructure,
	(pinnedChildrenByPosition, nonPinnedChildren, additionalData, comp, isOneDocMigrated) => {
		return JSON.stringify({
			pinnedChildrenByPosition,
			nonPinnedChildren,
			additionalData,
			comp,
			isOneDocMigrated,
		})
	}
)

const getChildrenGroupsWithPinnedLayer = (
	childrenIds: Array<string>,
	childrenLayout: { [compId: string]: Pick<Component['layout'], 'fixedPosition' | 'docked'> },
	comp: Component,
	additionalData: ComponentMetaData['pageMeshOverrides'] | ComponentMetaData['popupContainerMeshOverrides'],
	isOneDocMigrated: boolean
): Structure => {
	// TODO: this should move to CARMI and then we can remove the memoization
	const pinnedChildrenByPosition: PinnedChildrenByPositionMap = {}
	const nonPinnedChildren: Array<string> = []
	childrenIds.forEach((childId) => {
		const childLayout = childrenLayout[childId]
		const isPinned = checkIfPinnedComponentAndAddToMap(childId, childLayout, pinnedChildrenByPosition)
		if (!isPinned) {
			nonPinnedChildren.push(childId)
		}
	})

	return memoizedCreateStructure(pinnedChildrenByPosition, nonPinnedChildren, additionalData, comp, isOneDocMigrated)
}

const getChildrenGroupsAndPinnedLayer = (
	childrenIds: Array<string>,
	childrenLayout: { [compId: string]: SingleLayoutData },
	comp: Component,
	additionalData: ComponentMetaData['pageMeshOverrides'] | ComponentMetaData['popupContainerMeshOverrides'],
	isOneDocMigrated: boolean
): Structure => {
	// TODO: this should move to CARMI and then we can remove the memoization
	const pinnedChildrenByPosition: PinnedChildrenByPositionMap = {}
	const nonPinnedChildren: Array<string> = []
	childrenIds.forEach((childId) => {
		const childSingleLayout = childrenLayout[childId]
		const isPinned = isPinnedComponentAndAddToMap(childId, childSingleLayout, pinnedChildrenByPosition)
		if (!isPinned) {
			nonPinnedChildren.push(childId)
		}
	})

	return memoizedCreateStructure(pinnedChildrenByPosition, nonPinnedChildren, additionalData, comp, isOneDocMigrated)
}

const getResponsiveChildrenGroups = (
	comp: Component,
	childrenIds: Array<string>,
	childrenData: { [compId: string]: { layout: Component['layout']; isFixed: boolean; compType: string } }
): Structure => {
	const compId = comp.id
	const compComponents: Array<string> = []
	const compStructure = { [compId]: comp }

	if (childrenIds.length > 0) {
		const containerId = `${RESPONSIVE_PAGE_CONTAINER_PREFIX}${compId}`
		const containerComponents: Array<string> = []

		const createContainer = (
			id: string,
			parentId: string,
			componentType: string,
			childrenContainer: Array<string>,
			components: Array<string>
		) => {
			const compContainer = {
				id,
				components,
				componentType,
				layout: {},
				type: 'Container',
				parent: parentId,
				styleId: '',
				metaData: {},
			} as Component

			childrenContainer.push(id)
			compStructure[id] = compContainer
		}
		const createPinnedLayerContainer = (id: string, parentId: string, childrenContainer: Array<string>) =>
			createContainer(`${id}${PINNED_LAYER_SUFFIX}`, parentId, 'PinnedLayer', childrenContainer, [id])

		const headerSections = childrenIds.filter((id) => childrenData[id].compType === 'HeaderSection')
		const sections = childrenIds.filter((id) => childrenData[id].compType === 'Section')
		const footerSections = childrenIds.filter((id) => childrenData[id].compType === 'FooterSection')
		const others = childrenIds.filter(
			(id) => !headerSections.includes(id) && !sections.includes(id) && !footerSections.includes(id)
		)

		headerSections.forEach((id) => {
			childrenData[id].isFixed
				? createPinnedLayerContainer(id, containerId, containerComponents)
				: containerComponents.push(id)
		})

		if (sections.length > 0) {
			const pageSectionsChildren: Array<string> = []
			sections.forEach((id) => {
				childrenData[id].isFixed
					? createPinnedLayerContainer(id, `${SECTIONS_CONTAINER_PREFIX}${compId}`, pageSectionsChildren)
					: pageSectionsChildren.push(id)
			})
			createContainer(
				`${SECTIONS_CONTAINER_PREFIX}${compId}`,
				containerId,
				'PageSections',
				containerComponents,
				pageSectionsChildren
			)
		}

		footerSections.forEach((id) => {
			childrenData[id].isFixed
				? createPinnedLayerContainer(id, containerId, containerComponents)
				: containerComponents.push(id)
		})
		others.forEach((id) => {
			childrenData[id].isFixed
				? createPinnedLayerContainer(id, containerId, containerComponents)
				: containerComponents.push(id)
		})

		if (containerComponents.length > 0) {
			compStructure[containerId] = {
				id: containerId,
				components: containerComponents,
				componentType: 'ResponsiveContainer',
				layout: {},
				type: 'Container',
				parent: compId,
			} as Component
			compComponents.push(containerId)
		}
	}

	comp.components = compComponents
	return compStructure
}

export { getChildrenGroupsWithPinnedLayer, getChildrenGroupsAndPinnedLayer, getResponsiveChildrenGroups }
